import { FaceLandmarker, GestureRecognizer, FilesetResolver } from '@mediapipe/tasks-vision';
import { bodyPhase } from './hud';
import { S } from './state';
import { $, clamp, lerp } from './util';

export const MP = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
export const MODEL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
export const GMODEL = 'https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task';
export let landmarker = null, gesture = null, vision = null, gestureEvery = 0, video = null, lastVideoT = -1, previewLive = false, calibFrames = 0, calibAdvance = false;
export const lumC = document.createElement('canvas'); lumC.width = 240; lumC.height = 180; export const lumG = lumC.getContext('2d', {willReadFrequently:true});
export const camC = $('#camc'), camG = camC.getContext('2d');
export const L_EYE = [33,133,159,145,160,144,158,153], R_EYE = [362,263,386,374,385,380,387,373];
export let lastBlend = null;

export async function initFace(log, step){
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('no camera access in this context — open via http://localhost or Chrome file://');
  const camEl = step('Requesting webcam…');
  const stream = await navigator.mediaDevices.getUserMedia({video:{width:{ideal:640}, height:{ideal:480}, facingMode:'user'}, audio:false});
  video = $('#camvideo'); video.srcObject = stream; await video.play();
  await new Promise<void>((res, rej) => { const t0 = performance.now(); (function w(){ if (video.readyState >= 2 && video.videoWidth > 0) return res(); if (performance.now()-t0 > 8000) return rej(new Error('camera produced no frames')); requestAnimationFrame(w); })(); });
  log(`Webcam live · ${video.videoWidth}×${video.videoHeight}`, true, camEl); previewLive = true;
  if (landmarker) return;
  const modEl = step('Loading face model…');
  vision = await FilesetResolver.forVisionTasks(MP);
  const opts = (d): any => ({ baseOptions:{modelAssetPath:MODEL, delegate:d}, outputFaceBlendshapes:true, outputFacialTransformationMatrixes:false, runningMode:'VIDEO', numFaces:1 });
  try { landmarker = await FaceLandmarker.createFromOptions(vision, opts('GPU')); log('Face model ready · GPU', true, modEl); }
  catch(e){ landmarker = await FaceLandmarker.createFromOptions(vision, opts('CPU')); log('Face model ready · CPU', true, modEl); }
  const gEl = step('Loading hand gestures…');
  const gopts = (d): any => ({ baseOptions:{modelAssetPath:GMODEL, delegate:d}, runningMode:'VIDEO', numHands:1 });
  try { try { gesture = await GestureRecognizer.createFromOptions(vision, gopts('GPU')); } catch(e){ gesture = await GestureRecognizer.createFromOptions(vision, gopts('CPU')); } log('Hand gestures ready', true, gEl); }
  catch(e){ log('Hand gestures unavailable · keys 1/2/3 instead', false, gEl); }
}
export function avg(lm, idx){ let x=0,y=0; for (const i of idx){ x+=lm[i].x; y+=lm[i].y; } return {x:x/idx.length, y:y/idx.length}; }
export function patchStats(img, cx, cy, hw, hh){
  const W = img.width, H = img.height, d = img.data;
  const x0 = clamp(Math.round((cx-hw)*W),0,W-1), x1 = clamp(Math.round((cx+hw)*W),0,W-1);
  const y0 = clamp(Math.round((cy-hh)*H),0,H-1), y1 = clamp(Math.round((cy+hh)*H),0,H-1);
  const vals = []; let R=0, G=0, B=0;
  for (let y=y0;y<=y1;y++) for (let x=x0;x<=x1;x++){ const i=(y*W+x)*4; R+=d[i]; G+=d[i+1]; B+=d[i+2]; vals.push(0.299*d[i]+0.587*d[i+1]+0.114*d[i+2]); }
  const n = vals.length; if (!n) return {mean:0, std:0, top:0, r:0, g:0, b:0};
  R/=n; G/=n; B/=n; let s=0; for (const v of vals) s+=v; const mean = s/n;
  let q=0; for (const v of vals) q+=(v-mean)*(v-mean); const std = Math.sqrt(q/n);
  vals.sort((a,b)=>b-a); const k = Math.max(1, Math.round(n*0.1)); let t=0; for (let i=0;i<k;i++) t+=vals[i];
  return {mean, std, top:t/k, r:R, g:G, b:B};
}
export function lensFeatures(L){ return [L.ratio, L.contrast, L.sclera, L.rRel, L.bRel]; }
export function lensScore(L){
  const m = L.model; if (!m) return 0; const f = lensFeatures(L); let num=0, den=0;
  for (let i=0;i<f.length;i++){ const sep = m.off[i]-m.on[i]; if (Math.abs(sep)<1e-6) continue; const z = (f[i]-(m.on[i]+m.off[i])/2)/sep; num += m.w[i]*clamp(z,-2,2); den += m.w[i]; }
  return den? num/den : 0;
}
export function readFace(now){
  if (!landmarker || !video || video.readyState < 2) return;
  if (video.currentTime === lastVideoT) return; lastVideoT = video.currentTime;
  const res = landmarker.detectForVideo(video, now);
  const lm = res.faceLandmarks && res.faceLandmarks[0];
  if (!lm){ S.faceSeen = false; return; }
  S.faceSeen = true; lastBlend = res.faceBlendshapes && res.faceBlendshapes[0];
  const le = avg(lm, L_EYE), re = avg(lm, R_EYE);
  const ew = Math.hypot(lm[33].x-lm[133].x, lm[33].y-lm[133].y);
  lumG.drawImage(video, 0, 0, lumC.width, lumC.height);
  const img = lumG.getImageData(0,0,lumC.width,lumC.height);
  const hw = ew*0.85, hh = ew*0.55; const L = S.lum;
  const pl = patchStats(img, le.x, le.y, hw, hh), pr = patchStats(img, re.x, re.y, hw, hh);
  L.l = pl.mean; L.r = pr.mean;
  const c1 = patchStats(img, lm[50].x, lm[50].y, ew*.3, ew*.25), c2 = patchStats(img, lm[280].x, lm[280].y, ew*.3, ew*.25);
  L.skin = Math.max((c1.mean+c2.mean)/2, 8);
  const cR = (c1.r+c2.r)/2+1, cB = (c1.b+c2.b)/2+1, cL = L.skin;
  const eR = (pl.r+pr.r)/2+1, eB = (pl.b+pr.b)/2+1, eL = Math.max((pl.mean+pr.mean)/2, 4);
  const eye = (L.l+L.r)/2;
  L.ratio = eye / L.skin; L.contrast = ((pl.std+pr.std)/2) / Math.max(eye, 4); L.sclera = ((pl.top+pr.top)/2) / L.skin;
  L.rRel = (eR/eL)/(cR/cL); L.bRel = (eB/eL)/(cB/cL);
  L.score = lensScore(L); L.scoreS = lerp(L.scoreS, L.score, 0.35);
  if (S.calRun===null && S.phase==='play' && L.model){
    if (S.glassesOn && L.scoreS > 0.12) S.glassesOn = false;
    else if (!S.glassesOn && L.scoreS < -0.12) S.glassesOn = true;
  }
  const mid = {x:(lm[234].x+lm[454].x)/2, y:(lm[10].y+lm[152].y)/2};
  const fw = Math.hypot(lm[234].x-lm[454].x, lm[234].y-lm[454].y), fh = Math.hypot(lm[10].x-lm[152].x, lm[10].y-lm[152].y);
  const H = S.head; H.x = (lm[1].x - mid.x)/fw; H.y = (lm[1].y - mid.y)/fh;
  H.iris = ((lm[468].x-le.x)/ew + (lm[473].x-re.x)/ew)/2; H.irisY = ((lm[468].y-le.y)/ew + (lm[473].y-re.y)/ew)/2;
  const irisW = S.glassesOn ? 0 : 1;
  S.aimRaw.x = 0.5 - (H.x - H.restX)*S.gainHead - (H.iris - H.restIris)*S.gainIris*irisW;
  S.aimRaw.y = 0.5 + (H.y - H.restY)*S.gainHeadY + (H.irisY - H.restIrisY)*S.gainIrisY*irisW;
  if (lastBlend){ let bl=0, br=0, jaw=0; for (const c of lastBlend.categories){ if (c.categoryName==='eyeBlinkLeft') bl=c.score; else if (c.categoryName==='eyeBlinkRight') br=c.score; else if (c.categoryName==='jawOpen') jaw=c.score; } S.blink = bl > 0.45 && br > 0.45; S.jaw = jaw; }
  if (gesture && S.phase==='play' && (++gestureEvery % 3 === 0)){
    try { const g = gesture.recognizeForVideo(video, now); const top = g.gestures && g.gestures[0] && g.gestures[0][0];
      const name = top && top.score > 0.6 ? top.categoryName : '';
      if (name !== S.gesture){ S.gesture = name; S.gestureT = now; S.gestureFired = false; }
    } catch(e){}
  }
  drawCam(lm, le, re, hw, hh);
}
export function drawCam(lm, le, re, hw, hh){
  const W = camC.width, H = camC.height;
  camG.save(); camG.translate(W,0); camG.scale(-1,1); camG.drawImage(video, 0,0,W,H);
  camG.strokeStyle = S.glassesOn ? '#f5b642' : '#ff3b2f'; camG.lineWidth = 1.5;
  for (const e of [le,re]) camG.strokeRect((e.x-hw)*W, (e.y-hh)*H, hw*2*W, hh*2*H);
  camG.fillStyle = '#f2e9d6'; for (const i of [468,473]){ camG.beginPath(); camG.arc(lm[i].x*W, lm[i].y*H, 2, 0, 7); camG.fill(); }
  camG.restore();
  const sc = clamp(S.lum.scoreS, -1, 1);
  camG.fillStyle = 'rgba(0,0,0,.5)'; camG.fillRect(0, H-6, W, 6);
  camG.fillStyle = sc > 0 ? '#ff3b2f' : '#f5b642'; camG.fillRect(W/2, H-6, sc*W/2, 6);
  camG.fillStyle = 'rgba(242,233,214,.7)'; camG.fillRect(W/2-0.5, H-8, 1, 8);
}
export async function sampleFeatures(){
  const rows = [], hx=[], hy=[], ix=[], iy=[]; const KEEP = 45; calibAdvance = false;
  const fg = $('#calfg'), cnt = $('#calcount'), go = $('#calgo');
  const raf = () => new Promise(r => requestAnimationFrame(r));
  // 1. wait for Ready — nothing is read until the player says the lenses are where the step asks
  fg.style.strokeDashoffset = 283; cnt.textContent = '0';
  const tw = performance.now();
  while (!calibAdvance && performance.now()-tw < 180000){ await raf(); readFace(performance.now()); go.disabled = !S.faceSeen; go.textContent = S.faceSeen ? 'Ready' : 'No face'; }
  // 2. read ~1 s of frames; the ring is the read itself
  calibAdvance = false; go.disabled = true; go.textContent = 'Hold still…'; const t0 = performance.now();
  while (rows.length < KEEP && performance.now()-t0 < 15000){
    await raf(); readFace(performance.now());
    if (S.faceSeen){ rows.push(lensFeatures(S.lum)); hx.push(S.head.x); hy.push(S.head.y); ix.push(S.head.iris); iy.push(S.head.irisY); }
    fg.style.strokeDashoffset = 283*(1-rows.length/KEEP); cnt.textContent = String(rows.length);
  }
  fg.style.strokeDashoffset = 283; cnt.textContent = '0'; go.disabled = false; go.textContent = 'Ready';
  return {rows, hx, hy, ix, iy};
}
export const med = a => { const s=[...a].sort((p,q)=>p-q); return s[s.length>>1]; };
export const colStats = rows => { const k = rows[0].length, mean=[], std=[]; for (let i=0;i<k;i++){ const c = rows.map(r=>r[i]); const m = c.reduce((a,b)=>a+b)/c.length; mean.push(m); std.push(Math.sqrt(c.reduce((a,b)=>a+(b-m)*(b-m),0)/c.length)); } return {mean, std}; };
export function setCalibText(h, p, step?){ $('#calib h2').textContent = h; $('#calib p').textContent = p; if (step) $('#calstep').textContent = step; $('#calgo').style.visibility = step && step.startsWith('Step') ? '' : 'hidden'; }
export async function calibrateGlasses(){
  setCalibText('Put your sunglasses on', 'Lenses on, look at the screen, then press Ready — we read your lenses for a second.', 'Step 1 of 2');
  const on = await sampleFeatures(); calibFrames = on.rows.length; if (on.rows.length < 8) return false;
  setCalibText('Now take them off', 'Bare eyes on the screen, then press Ready.', 'Step 2 of 2');
  const off = await sampleFeatures(); if (off.rows.length < 8) return false;
  const a = colStats(on.rows), b = colStats(off.rows);
  const w = a.mean.map((_,i) => Math.abs(b.mean[i]-a.mean[i]) / (a.std[i] + b.std[i] + 1e-3));
  S.lum.model = {on:a.mean, off:b.mean, w};
  S.head.restX = med(on.hx.concat(off.hx)); S.head.restY = med(on.hy.concat(off.hy)); S.head.restIris = med(off.ix); S.head.restIrisY = med(off.iy);
  const sep = Math.max(...w); S.lum.sep = sep;
  if (sep < 0.9){ setCalibText('Both steps looked the same', 'Lenses on for step 1, off for step 2. Redoing.', 'Retry'); S.lum.model = null; await new Promise(r=>setTimeout(r,2200)); return calibrateGlasses(); }
  setCalibText('Locked', (sep > 2 ? 'Clear read on the lenses.' : 'Usable read — redo from the pause menu if it misfires.') + ' Put them back on to start.', 'Done');
  const t0 = performance.now();
  while (performance.now()-t0 < 5000){ await new Promise(r=>requestAnimationFrame(r)); readFace(performance.now()); if (S.faceSeen && S.lum.scoreS < -0.12) break; }
  S.glassesOn = true; S.lum.scoreS = -1; return true;
}
export async function recalibrate(){
  if (S.calRun || S.keyboard || S.phase!=='play') return;
  S.calRun = true; S.paused = true; S.glassesOn = true; document.body.classList.remove('off');
  $('#calib').classList.remove('hide'); await calibrateGlasses(); $('#calib').classList.add('hide');
  S.paused = false; S.calRun = null; bodyPhase('play');
}
export async function calibrateAim(){
  if (S.calRun || S.keyboard) return;
  const pts = [[.12,.14],[.88,.14],[.88,.86],[.12,.86]]; const el = $('#calpt'), msg = $('#calmsg');
  S.calRun = true; S.paused = true; S.cal = {ax:0,bx:1,ay:0,by:1}; el.style.display = 'block'; msg.style.display = 'block';
  const rawX=[], rawY=[], scrX=[], scrY=[];
  for (const [px,py] of pts){
    el.style.left = px*100+'%'; el.style.top = py*100+'%'; msg.textContent = 'look at the dot';
    const xs=[], ys=[]; const t0 = performance.now();
    while (performance.now()-t0 < 1700){ await new Promise(r => requestAnimationFrame(r)); if (performance.now()-t0 > 700 && S.faceSeen){ xs.push(S.aimRaw.x); ys.push(S.aimRaw.y); } }
    if (xs.length){ rawX.push(xs.reduce((a,b)=>a+b)/xs.length); rawY.push(ys.reduce((a,b)=>a+b)/ys.length); scrX.push(px); scrY.push(py); }
  }
  const fit = (r, s) => { const n=r.length; if (n<3) return null; const mr=r.reduce((a,b)=>a+b)/n, ms=s.reduce((a,b)=>a+b)/n; let num=0, den=0; for (let i=0;i<n;i++){ num+=(r[i]-mr)*(s[i]-ms); den+=(r[i]-mr)**2; } if (Math.abs(den) < 1e-6) return null; const b = num/den; return {a:ms-b*mr, b}; };
  const fx = fit(rawX, scrX), fy = fit(rawY, scrY);
  if (fx && fy && fx.b>0.3 && fy.b>0.3){ S.cal = {ax:fx.a,bx:fx.b,ay:fy.a,by:fy.b}; msg.textContent = 'calibrated'; } else msg.textContent = 'calibration failed — keep defaults';
  el.style.display = 'none'; setTimeout(()=>{ msg.style.display='none'; }, 1400); S.calRun = null; S.paused = false;
}

export function advanceCalib(){ calibAdvance = true; }
export function isPreviewLive(){ return previewLive; }
export function getVideo(){ return video; }
export function getCalibFrames(){ return calibFrames; }

